// 🔥 Firebase 기반 가격 관리 그리드 - 모델 그룹핑 UI/UX 리팩토링
// [배경] device_catalog + unified_prices를 기반으로 한 실시간 가격 관리
//        모델 단위 그룹핑 + 확장 가능한 가격 테이블 + 용량 관리 분리
//        고급 필터링, 벌크 업데이트, 인라인 편집, 통계, 히스토리 추적 모든 기능 포함
//        태블릿/스마트폰/웨어러블 공통 사용

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Snackbar,
  Fade,
  Backdrop,
  Modal,
  Avatar,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  Publish as ImportIcon,
  Assessment as StatsIcon,
  History as HistoryIcon,
  SelectAll as BulkIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  TrendingUp as TrendIcon,
  Close as CloseIcon,
  ShowChart as LineChartIcon,
  Analytics as ChartIcon,
  BarChart as BarChartIcon,
  Timeline as PieIcon,
  ExpandMore as ExpandAllIcon,
  ExpandLess as CollapseAllIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

import { DeviceCatalogService } from '../../../../services/deviceCatalogService';
import { getUnifiedPriceService } from '../../../../services/unifiedPriceAdapter';
import { 
  DeviceCatalogDocument, 
  generatePriceId,
  generateModelId,  // 🔑 P2: + 기호 보정을 위한 ID 재생성용
  extractPureModelId as extractPureModelIdFromCatalog,
  isCanonicalModelId,
  getConnectivityForPriceId,
  ConnectivityType
} from '../../../../types/deviceCatalog';
import { DeviceGrade, GradePriceInfo } from '../../../../types/gradeSystem';
import { IDAliasService } from '../../../../services/idAliasService';
// 🔑 SSOT Canonical ID 유틸 - 단일 진실 소스
import { 
  canonicalizeModelId, 
  buildCanonicalPriceId, 
  inferDeviceType,
  DeviceType as CanonicalDeviceType
} from '../../../../utils/idCanonical';

// 🔥 새로운 컴포넌트들
import ModelCard, { PriceRowData } from './ModelCard';
import ExpandablePriceTable from './ExpandablePriceTable';
import VariantManagerDrawer from './VariantManagerDrawer';
import VariantChipGroup from './VariantChipGroup';

// 🔥 기존 유틸리티 컴포넌트들
import { 
  PriceHistoryDialog, 
  PriceTemplateDialog, 
  PriceAnalysisDialog
} from './PriceManagementUtils';
import AddModelDialog from './AddModelDialog';
import DeleteModelDialog from './DeleteModelDialog';
import EditModelDialog from './EditModelDialog';
import { CellPosition, EditablePriceCellRef } from './EditablePriceCell';
import { useInlinePriceEdit, TableRowData } from './useInlinePriceEdit';

// 🔥 Props 인터페이스
interface FirebasePriceManagementGridProps {
  brand: 'Apple' | 'Samsung';
  deviceType: 'tablet' | 'smartphone' | 'wearable';
}

// 📊 테이블 행 데이터 (가격 관리용)
interface TableRow {
  modelId: string;
  modelName: string;
  series: string;
  storage: string;
  connectivity: string;
  priceId: string;
  prices: GradePriceInfo;
  hasPrice: boolean;
  isRecommended: boolean;
  releaseYear: number;
  priority: number;
}

// 📊 모델별 그룹 데이터
interface ModelGroup {
  model: DeviceCatalogDocument;
  priceRows: PriceRowData[];
  totalVariants: number;
  withPrice: number;
}

// 📊 필터 상태
interface FilterState {
  search: string;
  series: string;
  priceStatus: 'all' | 'set' | 'unset';
  recommended: 'all' | 'yes' | 'no';
}

// 📊 정렬 상태
interface SortState {
  field: 'modelName' | 'series' | 'priority' | 'releaseYear';
  direction: 'asc' | 'desc';
}

// 🎧 등급 명칭 반환 함수
const getGradeLabel = (grade: DeviceGrade): string => {
  return `${grade}등급`;
};

// 🔥 메인 컴포넌트
const FirebasePriceManagementGrid: React.FC<FirebasePriceManagementGridProps> = ({
  brand,
  deviceType
}) => {
  // 📊 핵심 데이터 상태
  const [models, setModels] = useState<DeviceCatalogDocument[]>([]);
  const [prices, setPrices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 📊 UI 상태
  const [showStats, setShowStats] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // 📊 필터 및 정렬 상태
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    series: '',
    priceStatus: 'all',
    recommended: 'all'
  });
  const [sort, setSort] = useState<SortState>({
    field: 'priority',
    direction: 'asc'
  });
  
  // 📊 모델 확장 상태 (어떤 모델이 펼쳐져 있는지)
  const [expandedModels, setExpandedModels] = useState<Set<string>>(new Set());
  
  // 📊 선택된 모델 상태 (벌크 편집용)
  const [selectedModels, setSelectedModels] = useState<Set<string>>(new Set());
  
  // 📊 다이얼로그 상태
  const [addModelDialogOpen, setAddModelDialogOpen] = useState(false);
  const [historyDialog, setHistoryDialog] = useState<{ open: boolean; priceId: string; modelName: string }>({
    open: false, priceId: '', modelName: ''
  });
  const [templateDialog, setTemplateDialog] = useState(false);
  const [analysisDialog, setAnalysisDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ 
    open: boolean; 
    modelId: string; 
    modelName: string; 
    targetPriceId?: string;
    targetVariant?: { storage: string; connectivity: string };
  }>({ open: false, modelId: '', modelName: '' });
  const [editModelDialog, setEditModelDialog] = useState<{
    open: boolean;
    model: DeviceCatalogDocument | null;
    initialPrices?: GradePriceInfo;
    targetConfig?: { storage: string; connectivity: string; priceId: string };
  }>({ open: false, model: null });
  
  // 📊 용량 관리 Drawer 상태
  const [variantDrawer, setVariantDrawer] = useState<{
    open: boolean;
    model: DeviceCatalogDocument | null;
  }>({ open: false, model: null });
  
  // 📊 벌크 편집 상태
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkEdit, setBulkEdit] = useState<{
    selectedRows: Set<string>;
    bulkPrices: GradePriceInfo;
    applyGrades: Set<DeviceGrade>;
  }>({
    selectedRows: new Set(),
    bulkPrices: { A: 0, B: 0, C: 0, D: 0, E: 0 },
    applyGrades: new Set(['A', 'B', 'C', 'D', 'E'] as DeviceGrade[])
  });
  
  // 📊 비활성(삭제된) 구성 관리 다이얼로그
  const [disabledDialogOpen, setDisabledDialogOpen] = useState(false);

  // 🆕 벌크 제조사 변경 상태
  const [bulkBrandChangeDialogOpen, setBulkBrandChangeDialogOpen] = useState(false);
  const [bulkBrandChangeTarget, setBulkBrandChangeTarget] = useState<'Apple' | 'Samsung' | 'LG'>('LG');
  
  // 📊 저장 중인 priceId Set
  const [saving, setSaving] = useState<Set<string>>(new Set());

  // 🔍 Firebase에서 데이터 로딩
  const loadData = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      if (forceRefresh) {
        DeviceCatalogService.clearCache();
        const UnifiedPriceService = getUnifiedPriceService();
        UnifiedPriceService.clearCache();
      }

      // 모델 조회
      const deviceModels = await DeviceCatalogService.getModelsByBrandAndCategory(
        brand, deviceType, { useCache: !forceRefresh }
      );

      // 🎧 웨어러블 '기본' → 구성 옵션 자동 변환 (1회성 마이그레이션)
      if (deviceType === 'wearable') {
        const WEARABLE_CONFIGS = ['풀세트', '유닛 세트', '유닛 1쪽', '본체만'];
        for (const model of deviceModels) {
          if (model.storageOptions.length === 1 && model.storageOptions[0] === '기본') {
            try {
              await DeviceCatalogService.updateModel(model.id, { storageOptions: WEARABLE_CONFIGS }, 'admin');
              // 기존 '기본' 가격 문서 삭제
              const oldPriceId = generatePriceId(model.id, { storage: '기본' }, 'KR');
              const { db } = await import('../../../../firebase');
              const { deleteDoc, doc } = await import('firebase/firestore');
              try { await deleteDoc(doc(db, 'unified_prices', oldPriceId)); } catch {}
              // 로컬 데이터 즉시 반영
              model.storageOptions = WEARABLE_CONFIGS;
              console.log(`🎧 [자동 변환] ${model.modelKo}: 기본 → 풀세트/유닛세트/유닛1쪽/본체만`);
            } catch (e) {
              console.error(`❌ [자동 변환 실패] ${model.modelKo}:`, e);
            }
          }
        }
      }

      setModels(deviceModels);

      // 가격 조회
      const UnifiedPriceService = getUnifiedPriceService();
      UnifiedPriceService.clearCache();
      const priceData = await UnifiedPriceService.getPricesForManagement(brand, deviceType);
      
      // 핵심 로그: 로딩 결과 요약만 출력
      console.log(`📊 [가격관리] ${brand} ${deviceType}: 모델 ${deviceModels.length}개, 가격 ${priceData.length}개 로딩 완료`);
      
      setPrices(priceData);

    } catch (err) {
      console.error(`❌ Firebase 데이터 로딩 실패:`, err);
      setError(`${brand} ${deviceType} 데이터 로딩 실패: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [brand, deviceType]);

  // 컴포넌트 마운트 시 데이터 로딩
  useEffect(() => {
    loadData();
  }, [loadData]);

  // 🔄 브랜드/디바이스 변경 시 상태 초기화
  useEffect(() => {
    setExpandedModels(new Set());
    setSelectedModels(new Set());
    setBulkEdit(prev => ({ ...prev, selectedRows: new Set() }));
  }, [brand, deviceType]);

  // 📊 테이블 행 데이터 생성 (기존 호환성 유지)
  // 🔥 버그 수정: storageOptions 배열의 중복 제거 (Set 활용)
  const tableRows = useMemo(() => {
    const rows: TableRow[] = [];
    // 🔥 중복 priceId 추적용 Set (같은 priceId가 여러 번 추가되는 것 방지)
    const addedPriceIds = new Set<string>();
    // 📊 디버깅용 카운터 (요약 로그용)
    let storageDuplicateModels = 0;
    let skippedDuplicatePriceIds = 0;
    let wearableMatchFailures: string[] = [];
    let tabletMatchFailures: string[] = [];

    // 🔥 SSOT: 오염된 modelId를 canonical 형식으로 정제
    // - storage, connectivity, 순번 제거
    // - IDAliasService.extractPureModelId() 사용 (중앙 집중 관리)
    // 예: "tablet-samsung-galaxy-tab-s-galaxy-tab-s9+-256gb-wifi-139" → "tablet-samsung-galaxy-tab-s-galaxy-tab-s9+"
    const extractPureModelId = (modelId: string, targetDeviceType?: string, _modelBrand?: string): string => {
      // 🎧 웨어러블 예외: 세대 번호(-1, -2, -pro-2 등)가 모델 구분의 핵심!
      // extractPureModelId를 적용하면 세대 번호가 "순번"으로 오인되어 제거됨
      // 예: apple-wearable-airpods-1 → apple-wearable-airpods (잘못!)
      // 웨어러블은 원본 modelId를 그대로 사용
      if (targetDeviceType === 'wearable') {
        return modelId;
      }
      
      // 🔑 SSOT: deviceCatalog.ts의 extractPureModelId 사용 (중앙 집중 관리)
      if (isCanonicalModelId(modelId)) {
        return modelId; // 이미 canonical이면 그대로 반환
      }
      return extractPureModelIdFromCatalog(modelId);
    };
    
    // 🔑 P2 핵심: model.id가 + 기호를 누락한 경우 model.modelKo/modelEn에서 복원
    // [배경] Firestore에 저장된 model.id가 이미 +가 없는 상태로 저장된 경우,
    //        canonicalizeModelId를 호출해도 복구 불가능하므로 모델명에서 재생성 필요
    const correctModelIdWithPlusSign = (model: typeof models[0]): string => {
      const modelName = model.modelKo || model.modelEn || '';
      const currentId = model.id;
      const idLower = currentId.toLowerCase();
      
      // + 기호 감지 (모델명에서)
      const hasPlusPlus = modelName.includes('++') || modelName.includes('플러스플러스');
      const hasPlus = !hasPlusPlus && (modelName.includes('+') || modelName.includes('플러스'));
      
      // ID에 이미 올바르게 반영되어 있는지 확인
      const idHasPlusPlus = idLower.includes('plus-plus');
      const idHasPlus = !idHasPlusPlus && idLower.includes('-plus');
      
      // 보정이 필요한 경우 (모델명에는 +가 있지만 ID에는 없음)
      if ((hasPlusPlus && !idHasPlusPlus) || (hasPlus && !idHasPlus)) {
        // generateModelId를 사용하여 올바른 canonical ID 재생성
        const correctedId = generateModelId({
          brand: model.brand,
          deviceType: model.deviceType,
          series: model.series,
          modelEn: model.modelEn || modelName
        });
        
        console.log(`🔧 [P2 ID 보정] "${modelName}": "${currentId}" → "${correctedId}"`);
        return correctedId;
      }
      
      // 보정 불필요 - 원본 반환
      return currentId;
    };

    models.forEach(model => {
      // 🔥 storageOptions에서 중복 제거 (버그 수정 핵심)
      const rawStorageOptions = model.storageOptions;
      const effectiveStorageOptions = Array.from(new Set(rawStorageOptions));
      
      // 🔥 connectivityOptions에서도 중복 제거
      const rawConnectivityOptions = deviceType === 'wearable'
        ? ['기본']
        : (model.connectivityOptions || ['Wi-Fi']);
      const effectiveConnectivityOptions = Array.from(new Set(rawConnectivityOptions));

      // 🔥 중복 제거 카운트 (개별 로그 대신 카운터)
      if (rawStorageOptions.length !== effectiveStorageOptions.length) {
        storageDuplicateModels++;
      }

      effectiveStorageOptions.forEach(storage => {
        effectiveConnectivityOptions.forEach(connectivity => {
          // 비활성화된 구성 스킵
          const disabled = (model as any).disabledVariants?.some(
            (v: any) => v.storage === storage &&
              ((v.connectivity || 'Wi-Fi') === (connectivity || 'Wi-Fi'))
          ) ?? false;
          if (disabled) return;

          // 🔥 가격 데이터 찾기 (priceId 기반 매칭 우선, fallback으로 modelId+storage 매칭)
          let matchingPrice;
          
          // 🔥 헬퍼: 여러 매칭 중 최적의 문서 선택
          // 🔑 P0 핵심 수정: canonical + LTE 문서 최우선 (중복 문서 문제 해결)
          const selectBestPrice = (candidates: typeof prices) => {
            if (candidates.length === 0) return undefined;
            if (candidates.length === 1) return candidates[0];
            
            // 1) 가격이 설정된 문서만 필터링
            const withPrice = candidates.filter(p => 
              p.gradePrices && (p.gradePrices.A > 0 || p.gradePrices.B > 0 || 
                               p.gradePrices.C > 0 || p.gradePrices.D > 0)
            );
            
            const pool = withPrice.length > 0 ? withPrice : candidates;
            if (pool.length === 1) return pool[0];
            
            // 🔑 2-1) 스마트폰: canonical + LTE 문서 최우선!
            // 이것이 가장 정확한 SSOT 문서 (samsung-smartphone-...-lte-kr)
            if (deviceType === 'smartphone') {
              const canonicalLteDocs = pool.filter(p => {
                const id = (p.id || '').toLowerCase();
                const isCanonical = id.startsWith('samsung-') || id.startsWith('apple-');
                const hasLte = id.includes('-lte-');
                return isCanonical && hasLte;
              });
              
              if (canonicalLteDocs.length >= 1) {
                // 여러 개면 updatedAt 최신 선택
                if (canonicalLteDocs.length === 1) return canonicalLteDocs[0];
                const sorted = [...canonicalLteDocs].sort((a, b) => {
                  const aTime = a.updatedAt?.seconds || a.updatedAt?.getTime?.() / 1000 || 0;
                  const bTime = b.updatedAt?.seconds || b.updatedAt?.getTime?.() / 1000 || 0;
                  return bTime - aTime;
                });
                return sorted[0];
              }
            }
            
            // 2-2) canonical 형식(brand-deviceType-...) 문서 우선
            const canonicalDocs = pool.filter(p => {
              const id = (p.id || '').toLowerCase();
              return id.startsWith('samsung-') || id.startsWith('apple-');
            });
            
            if (canonicalDocs.length === 1) return canonicalDocs[0];
            
            // 3) 여러 canonical 문서가 있으면 updatedAt 최신 우선
            const finalPool = canonicalDocs.length > 0 ? canonicalDocs : pool;
            const sorted = [...finalPool].sort((a, b) => {
              const aTime = a.updatedAt?.seconds || a.updatedAt?.getTime?.() / 1000 || 0;
              const bTime = b.updatedAt?.seconds || b.updatedAt?.getTime?.() / 1000 || 0;
              return bTime - aTime; // 최신이 먼저
            });
            
            return sorted[0];
          };
          
          // 🔥 순수 modelId 추출 (Samsung 태블릿 id에 storage가 포함된 문제 대응)
          // ⚠️ Apple 태블릿은 원본 id 그대로 사용
          // 🔑 P2 핵심: model.id가 + 기호를 누락한 경우 먼저 보정 후 extractPureModelId 적용
          const correctedModelId = correctModelIdWithPlusSign(model);
          const pureModelId = extractPureModelId(correctedModelId, deviceType, model.brand);
          
          // 🔥 1단계: modelId + storage로 정확한 매칭 (가장 안정적)
          // ⚠️ 수정: storage도 함께 매칭하여 같은 모델의 다른 용량이 잘못 매칭되는 버그 수정
          // ⚠️ Samsung 태블릿: model.id와 price.modelId 모두 순수 modelId로 비교
          
          // 🔥 storage 비교 헬퍼 (대소문자/공백 무시, 한글 보존)
          const normalizeStorage = (s: string | undefined): string => {
            if (!s) return '';
            return s.toLowerCase().replace(/[^a-z0-9가-힣]/g, '');
          };
          const normalizedTargetStorage = normalizeStorage(storage);
          
          // 🔥 connectivity 비교 헬퍼 (Wi-Fi vs Cellular 정확히 구분!)
          const matchConnectivityStrict = (priceConn: string | undefined, targetConn: string): boolean => {
            const pConn = (priceConn || 'Wi-Fi').toLowerCase().replace(/\s+/g, '');
            const tConn = (targetConn || 'Wi-Fi').toLowerCase().replace(/\s+/g, '');
            
            // Wi-Fi 모델끼리 매칭
            const pIsWifi = !pConn || pConn === 'wi-fi' || pConn === 'wifi';
            const tIsWifi = !tConn || tConn === 'wi-fi' || tConn === 'wifi' || tConn === '기본';
            if (pIsWifi && tIsWifi) return true;
            
            // Cellular 모델끼리 매칭
            const pIsCellular = pConn.includes('cellular') || pConn.includes('lte') || pConn.includes('5g');
            const tIsCellular = tConn.includes('cellular') || tConn.includes('lte') || tConn.includes('5g');
            if (pIsCellular && tIsCellular) return true;
            
            // Wi-Fi와 Cellular는 매칭되면 안됨!
            return false;
          };
          
          // 🔑 SSOT: modelId를 항상 canonical 형식으로 정규화 (idCanonical.ts 사용)
          // canonical 형식: {brand}-{deviceType}-{series}-{modelEn}
          // 예: samsung-smartphone-galaxy-s-galaxy-s25
          // ⚠️ 기존 normalizeModelIdFormat은 SSOT와 반대로 뒤집어서 중복 원인이었음 → 완전 제거
          // 🔑 P2 핵심: model.id 대신 보정된 correctedModelId 사용!
          const normalizedCatalogModelId = canonicalizeModelId(correctedModelId);
          const normalizedPureModelId = canonicalizeModelId(pureModelId);
          
          const modelIdMatches = prices.filter(price => {
            const pricePureModelId = extractPureModelId(price.modelId || '', deviceType, price.brand);
            
            // 🔑 SSOT: canonical 형식으로 정규화 후 비교 (idCanonical.ts 사용)
            const normalizedPriceModelId = canonicalizeModelId(price.modelId || '');
            const normalizedPricePureModelId = canonicalizeModelId(pricePureModelId);
            
            // 🔑 P2 핵심: model.id 대신 보정된 correctedModelId 사용!
            const modelIdMatch = 
                   // 보정된 ID로 비교 (P2 핵심 수정!)
                   price.modelId === correctedModelId || 
                   price.modelId === pureModelId ||
                   pricePureModelId === pureModelId ||
                   // 🔥 정규화된 형식으로 비교 (핵심 수정!)
                   normalizedPriceModelId === normalizedCatalogModelId ||
                   normalizedPriceModelId === normalizedPureModelId ||
                   normalizedPricePureModelId === normalizedPureModelId;
            
            // 🔥 버그 수정: 웨어러블이 아닌 경우 storage도 함께 매칭
            // storage 정규화하여 대소문자/공백 차이 허용 (예: '32GB' vs '32gb' vs '32 GB')
            const normalizedPriceStorage = normalizeStorage(price.variant?.storage);
            const storageMatch = normalizedPriceStorage === normalizedTargetStorage;
            
            // 🔥 핵심 버그 수정: 태블릿은 connectivity도 정확히 매칭해야 함!
            // Wi-Fi와 Cellular 가격이 섞이면 안됨
            const connectivityMatch = matchConnectivityStrict(price.variant?.connectivity, connectivity);
            
            return modelIdMatch && storageMatch && connectivityMatch;
          });
          
          if (modelIdMatches.length > 0) {
            // 가격이 설정된 문서 우선 선택!
            matchingPrice = selectBestPrice(modelIdMatches);
          }
          
          // 🔥 1-2단계: priceId로 매칭 시도 (modelId 매칭 실패 시)
          // 🔑 P0 수정: canonical priceId로 먼저 시도, 실패 시 레거시 형식으로 폴백
          if (!matchingPrice) {
            const expectedVariant = {
              storage: storage,
              connectivity: deviceType === 'smartphone' ? 'LTE'  // 🔑 스마트폰은 항상 LTE
                : (deviceType === 'wearable' || connectivity === 'Wi-Fi' || connectivity === '기본')
                  ? undefined : connectivity
            };
            
            // 🔑 P0: canonical modelId로 priceId 생성 (우선)
            const canonicalPureModelId = canonicalizeModelId(pureModelId);
            const canonicalExpectedPriceId = generatePriceId(canonicalPureModelId, expectedVariant, 'KR');
            let priceIdMatches = prices.filter(price => price.id === canonicalExpectedPriceId);
            
            // 🔑 P0: canonical 매칭 실패 시 레거시 형식으로 폴백 (하위 호환)
            if (priceIdMatches.length === 0) {
              const legacyExpectedPriceId = generatePriceId(pureModelId, expectedVariant, 'KR');
              if (legacyExpectedPriceId !== canonicalExpectedPriceId) {
                priceIdMatches = prices.filter(price => price.id === legacyExpectedPriceId);
              }
            }
            
            matchingPrice = selectBestPrice(priceIdMatches);
          }
          
          // 🔥 2단계: priceId 매칭 실패 시, modelId+storage 기반 매칭 (호환성)
          // 🔑 P2 핵심: model.id 대신 보정된 correctedModelId 사용!
          if (!matchingPrice) {
            if (deviceType === 'smartphone') {
              // 🔑 P2 수정: 보정된 modelId와 canonical modelId 모두 비교
              matchingPrice = prices.find(price => {
                const normalizedPriceModelId = canonicalizeModelId(price.modelId || '');
                const modelIdMatch = 
                  price.modelId === correctedModelId ||
                  price.modelId === pureModelId ||
                  normalizedPriceModelId === normalizedCatalogModelId;
                return modelIdMatch && price.variant?.storage === storage;
              });
            } else if (deviceType === 'wearable') {
              // 🎧 웨어러블 매칭: 한글/영어 혼합 대응 + 특수 키워드 검증 + 가격 있는 문서 우선
              
              // 🔥 한글 제거하고 영어/숫자만 추출 (핵심 수정!)
              const extractEnglishKeywords = (id: string | undefined): string[] => {
                if (!id) return [];
                // 한글/특수문자 제거 후 소문자로 변환
                const englishOnly = id.toLowerCase().replace(/[^a-z0-9\-_]/g, '-');
                // 연속 하이픈 정리 및 빈 문자열 제거
                return englishOnly.split(/[-_]+/).filter(k => k && k.length > 0);
              };
              
              // 🔑 P2 핵심: model.id 대신 보정된 correctedModelId 사용!
              const modelKeywords = extractEnglishKeywords(correctedModelId);
              
              // 🔥 특수 키워드 헬퍼 함수 (모든 매칭에서 사용)
              const specialKeywords = ['pro', 'max', 'anc'];
              const modelHasSpecials = specialKeywords.filter(s => modelKeywords.includes(s));
              
              const checkSpecialKeywords = (priceKeywords: string[]): boolean => {
                const priceHasSpecials = specialKeywords.filter(s => priceKeywords.includes(s));
                // 특수 키워드 집합이 완전히 일치해야 함
                if (modelHasSpecials.length !== priceHasSpecials.length) return false;
                return modelHasSpecials.every(s => priceHasSpecials.includes(s));
              };
              
              // 🔥 모든 매칭 후보 수집 (filter 사용)
              const matchingCandidates = prices.filter(price => {
                // 🎧 구성별 storage 매칭 필수 (풀세트/유닛세트/본체만 구분)
                const priceStorage = normalizeStorage(price.variant?.storage);
                if (priceStorage !== normalizedTargetStorage) return false;

                const priceKeywords = extractEnglishKeywords(price.id);
                const priceModelKeywords = extractEnglishKeywords(price.modelId);

                // 🔥 모든 매칭에서 특수 키워드 검증 선행!
                if (!checkSpecialKeywords(priceKeywords)) {
                  return false; // pro/max/anc 불일치 시 즉시 실패
                }

                // 🔥 핵심 키워드 매칭 (airpods, 세대 숫자 등)
                // 숫자(세대번호)는 길이 1이어도 보존
                const coreModelKeywords = modelKeywords.filter(k =>
                  k !== 'apple' && k !== 'wearable' && k !== 'samsung' && (k.length > 1 || /^\d+$/.test(k))
                );
                const allPriceKeywords = [...priceKeywords, ...priceModelKeywords];

                const matchedCount = coreModelKeywords.filter(mk =>
                  allPriceKeywords.some(pk => pk.includes(mk) || mk.includes(pk))
                ).length;

                const matchRatio = coreModelKeywords.length > 0
                  ? matchedCount / coreModelKeywords.length
                  : 0;

                return matchRatio >= 0.8;
              });
              
              // 🔥 가격이 설정된 문서 우선 선택!
              matchingPrice = selectBestPrice(matchingCandidates);
              
            } else if (deviceType === 'tablet') {
              // 📱 태블릿: 유연한 매칭 (Samsung 태블릿 ID 형식 불일치 대응)
              // 🔥 Samsung device_catalog ID가 이미 storage/connectivity를 포함한 형식일 수 있음
              //    예: tablet-samsung-galaxy-tab-s-galaxy-tab-s9-128gb-wifi-141
              //    이런 경우 modelKo 기반 매칭이 필요
              
              // 🔑 P2 핵심: model.id 대신 보정된 correctedModelId 사용!
              const normalizedModelId = correctedModelId.toLowerCase();
              const normalizedStorage = storage.toLowerCase().replace(/[^a-z0-9]/g, '');
              
              // 🔥 modelKo에서 핵심 키워드 추출 헬퍼
              // 예: "갤럭시 탭 S9 울트라" → ["galaxy", "tab", "s9", "ultra"]
              const extractTabletKeywords = (modelKo: string | undefined): string[] => {
                if (!modelKo) return [];
                const korToEng: Record<string, string> = {
                  '갤럭시': 'galaxy', '탭': 'tab', '울트라': 'ultra', 
                  '라이트': 'lite', '플러스': 'plus', '액티브': 'active',
                  '프로': 'pro'
                };
                
                let text = modelKo.toLowerCase();
                // 한글→영어 변환
                Object.entries(korToEng).forEach(([kor, eng]) => {
                  text = text.replace(new RegExp(kor, 'g'), eng);
                });
                
                // 영숫자만 추출하고 분리
                return text
                  .replace(/[^a-z0-9\s\-+]/g, ' ')
                  .split(/[\s\-]+/)
                  .filter(k => k.length > 0);
              };
              
              // connectivity 비교 헬퍼 (엄격한 비교 - Wi-Fi와 Cellular 완전 분리!)
              const matchConnectivity = (priceConn: string | undefined, targetConn: string): boolean => {
                const pConn = (priceConn || 'Wi-Fi').toLowerCase().replace(/\s+/g, '');
                const tConn = (targetConn || 'Wi-Fi').toLowerCase().replace(/\s+/g, '');
                
                // Wi-Fi 모델 판별
                const pIsWifi = !pConn || pConn === 'wi-fi' || pConn === 'wifi';
                const tIsWifi = !tConn || tConn === 'wi-fi' || tConn === 'wifi' || tConn === '기본';
                if (pIsWifi && tIsWifi) return true;
                
                // Cellular 모델 판별 (wificellular 포함!)
                const pIsCellular = pConn.includes('cellular') || pConn.includes('lte') || pConn.includes('5g');
                const tIsCellular = tConn.includes('cellular') || tConn.includes('lte') || tConn.includes('5g');
                if (pIsCellular && tIsCellular) return true;
                
                // Wi-Fi와 Cellular는 절대 매칭되면 안됨!
                return false;
              };
              
              // 🔥 태블릿 키워드 매칭 헬퍼
              const matchTabletKeywords = (priceIdOrModelId: string, keywords: string[]): boolean => {
                if (keywords.length === 0) return false;
                const priceLower = priceIdOrModelId.toLowerCase();
                
                // 핵심 키워드 (galaxy, tab 제외하고 모델 구분 키워드)
                const coreKeywords = keywords.filter(k => 
                  k !== 'galaxy' && k !== 'tab' && k !== 'samsung' && k.length > 1
                );
                
                // 시리즈 키워드 (s9, a9, s8 등)
                const seriesKeyword = coreKeywords.find(k => /^[sa]\d/.test(k));
                
                // 특수 키워드 (ultra, lite, plus, fe, active, pro 등)
                const specialKeywords = coreKeywords.filter(k => 
                  ['ultra', 'lite', 'plus', 'fe', 'active', 'pro', '5g'].includes(k)
                );
                
                // 시리즈 필수 매칭
                if (seriesKeyword && !priceLower.includes(seriesKeyword)) return false;
                
                // 특수 키워드 양방향 검증 (ultra 있는 모델은 ultra 가격에만 매칭)
                const priceSpecials = ['ultra', 'lite', 'plus', 'fe', 'active', 'pro', '5g'].filter(s => priceLower.includes(s));
                if (specialKeywords.length !== priceSpecials.length) return false;
                if (!specialKeywords.every(s => priceSpecials.includes(s))) return false;
                
                return true;
              };
              
              const modelKeywords = extractTabletKeywords(model.modelKo);
              
              matchingPrice = prices.find(price => {
                // 1) 정확한 modelId 매칭 (기존 로직)
                // 🔑 P2 핵심: model.id 대신 보정된 correctedModelId 사용!
                if ((price.modelId === correctedModelId || price.modelId === pureModelId) && 
                    price.variant?.storage === storage && 
                    matchConnectivity(price.variant?.connectivity, connectivity)) {
                  return true;
                }
                
                // 2) storage 매칭 확인 (필수)
                const priceStorage = (price.variant?.storage || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                if (priceStorage !== normalizedStorage) return false;
                
                // 3) connectivity 매칭 확인 (필수)
                if (!matchConnectivity(price.variant?.connectivity, connectivity)) return false;
                
                // 4) modelKo 키워드 기반 매칭 (Samsung 태블릿용 핵심 로직)
                const priceIdLower = price.id?.toLowerCase() || '';
                const priceModelIdLower = price.modelId?.toLowerCase() || '';
                
                if (matchTabletKeywords(priceIdLower, modelKeywords) ||
                    matchTabletKeywords(priceModelIdLower, modelKeywords)) {
                  return true;
                }
                
                // 5) 기존 유연한 매칭 (폴백)
                if (priceIdLower.includes(normalizedModelId) || 
                    priceModelIdLower.includes(normalizedModelId) ||
                    normalizedModelId.includes(priceModelIdLower.replace(/-+$/, ''))) {
                  return true;
                }
                
                return false;
              });
            } else {
              // 🔑 P0 수정: smartphone도 canonical 정규화 비교 + selectBestPrice 사용!
              // 기존 prices.find는 첫 번째 매칭만 반환 → 레거시 문서 우선 문제
              const smartphoneMatches = prices.filter(price => {
                // 🔥 canonical 형식으로 정규화하여 비교 (핵심!)
                const normalizedPriceModelId = canonicalizeModelId(price.modelId || '');
                // 🔑 P2 핵심: model.id 대신 보정된 correctedModelId 사용!
                const modelIdMatch = 
                  price.modelId === correctedModelId ||
                  price.modelId === pureModelId ||
                  normalizedPriceModelId === normalizedCatalogModelId;
                const storageMatch = price.variant?.storage === storage ||
                  normalizeStorage(price.variant?.storage) === normalizedTargetStorage;
                return modelIdMatch && storageMatch;
              });
              matchingPrice = selectBestPrice(smartphoneMatches);
            }
          }
          
          // 🔥 3단계: 여전히 없으면, priceId에 model.id가 포함된 유연한 매칭
          // 🔑 P2 핵심: model.id 대신 보정된 correctedModelId 사용!
          if (!matchingPrice) {
            const normalizedModelId = correctedModelId.toLowerCase();
            
            if (deviceType === 'wearable') {
              // 🎧 웨어러블: 한글 제거 후 영어 키워드로 매칭 (3단계)
              const extractEnglishOnly = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
              const modelEnglishId = extractEnglishOnly(normalizedModelId);
              
              const specialKeywords = ['pro', 'max', 'anc'];
              const modelHasSpecial = specialKeywords.filter(s => modelEnglishId.includes(s));
              
              const candidates3 = prices.filter(price => {
                // 🎧 구성별 storage 매칭 필수
                const priceStorage = normalizeStorage(price.variant?.storage);
                if (priceStorage !== normalizedTargetStorage) return false;

                const priceIdLower = extractEnglishOnly(price.id || '');

                // 특수 키워드 양방향 검증
                const priceHasSpecial = specialKeywords.filter(s => priceIdLower.includes(s));
                if (modelHasSpecial.length !== priceHasSpecial.length) return false;
                if (!modelHasSpecial.every(s => priceHasSpecial.includes(s))) return false;

                // 핵심 키워드 포함 검사 (airpods + 세대)
                const hasAirpods = priceIdLower.includes('airpods');
                const modelGen = modelEnglishId.match(/\d+/)?.[0];
                const priceGen = priceIdLower.match(/\d+/)?.[0];

                return hasAirpods && modelGen === priceGen;
              });
              
              // 🔥 가격이 설정된 문서 우선 선택!
              matchingPrice = selectBestPrice(candidates3);
            } else if (deviceType === 'tablet') {
              // 📱 태블릿 3단계: modelKo 기반 키워드 매칭 (폴백)
              const normalizedStorage = storage.toLowerCase().replace(/[^a-z0-9]/g, '');
              
              // 🔥 modelKo에서 핵심 키워드 추출 (2단계와 동일 로직)
              const korToEng: Record<string, string> = {
                '갤럭시': 'galaxy', '탭': 'tab', '울트라': 'ultra', 
                '라이트': 'lite', '플러스': 'plus', '액티브': 'active', '프로': 'pro'
              };
              let modelText = (model.modelKo || '').toLowerCase();
              Object.entries(korToEng).forEach(([kor, eng]) => {
                modelText = modelText.replace(new RegExp(kor, 'g'), eng);
              });
              const modelKeywords3 = modelText.replace(/[^a-z0-9\s\-+]/g, ' ').split(/[\s\-]+/).filter(k => k.length > 0);
              
              // 시리즈/특수 키워드 추출
              const coreKeywords3 = modelKeywords3.filter(k => k !== 'galaxy' && k !== 'tab' && k !== 'samsung' && k.length > 1);
              const seriesKeyword3 = coreKeywords3.find(k => /^[sa]\d/.test(k));
              const specialKeywords3 = coreKeywords3.filter(k => ['ultra', 'lite', 'plus', 'fe', 'active', 'pro', '5g'].includes(k));
              
              // 🔥 connectivity 체크 헬퍼 (3단계용 - 핵심 버그 수정!)
              const matchConn3 = (priceConn: string | undefined, targetConn: string): boolean => {
                const pConn = (priceConn || 'Wi-Fi').toLowerCase().replace(/\s+/g, '');
                const tConn = (targetConn || 'Wi-Fi').toLowerCase().replace(/\s+/g, '');
                
                const pIsWifi = !pConn || pConn === 'wi-fi' || pConn === 'wifi';
                const tIsWifi = !tConn || tConn === 'wi-fi' || tConn === 'wifi' || tConn === '기본';
                if (pIsWifi && tIsWifi) return true;
                
                const pIsCellular = pConn.includes('cellular') || pConn.includes('lte') || pConn.includes('5g') || pConn.includes('wificellular');
                const tIsCellular = tConn.includes('cellular') || tConn.includes('lte') || tConn.includes('5g');
                if (pIsCellular && tIsCellular) return true;
                
                return false;
              };
              
              const candidates3Tablet = prices.filter(price => {
                const priceIdLower = price.id?.toLowerCase() || '';
                const priceStorage = (price.variant?.storage || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                
                // storage 매칭 필수
                if (priceStorage !== normalizedStorage) return false;
                
                // 🔥 핵심 버그 수정: connectivity 매칭 필수!
                if (!matchConn3(price.variant?.connectivity, connectivity)) return false;
                
                // 시리즈 키워드 필수 매칭
                if (seriesKeyword3 && !priceIdLower.includes(seriesKeyword3)) return false;
                
                // 특수 키워드 양방향 검증
                const priceSpecials3 = ['ultra', 'lite', 'plus', 'fe', 'active', 'pro', '5g'].filter(s => priceIdLower.includes(s));
                if (specialKeywords3.length !== priceSpecials3.length) return false;
                if (!specialKeywords3.every(s => priceSpecials3.includes(s))) return false;
                
                return true;
              });
              
              // 🔥 가격이 설정된 문서 우선 선택!
              matchingPrice = selectBestPrice(candidates3Tablet);
              
              // 폴백: 기존 로직 + connectivity 체크 추가!
              if (!matchingPrice) {
                matchingPrice = prices.find(price => {
                  const priceIdLower = price.id?.toLowerCase() || '';
                  const priceStorage = (price.variant?.storage || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                  if (priceStorage !== normalizedStorage) return false;
                  
                  // 🔥 핵심 버그 수정: connectivity 매칭 필수!
                  if (!matchConn3(price.variant?.connectivity, connectivity)) return false;
                  
                  return priceIdLower.includes(normalizedModelId) || 
                         normalizedModelId.includes(priceIdLower.replace(/-kr$/, '').replace(/-+$/, ''));
                });
              }
            } else {
              // 🔑 P0 수정: 비웨어러블/비태블릿도 selectBestPrice 사용!
              const stage3Matches = prices.filter(price => {
                const priceIdLower = price.id?.toLowerCase() || '';
                const idMatch = priceIdLower.includes(normalizedModelId) || 
                  normalizedModelId.includes(priceIdLower.replace('-kr', ''));
                const storageMatch = price.variant?.storage === storage ||
                  normalizeStorage(price.variant?.storage) === normalizedTargetStorage;
                return idMatch && storageMatch;
              });
              matchingPrice = selectBestPrice(stage3Matches);
            }
          }
          
          // 🔥 4단계: 웨어러블 전용 - 최종 폴백 매칭 (모델명 핵심 키워드로)
          if (!matchingPrice && deviceType === 'wearable') {
            // 🔥 모델명에서 핵심 정보 추출 (예: "AirPods Pro 2세대" → airpods, pro, 2)
            const extractCoreInfo = (s: string) => {
              const lower = s.toLowerCase();
              const hasAirpods = lower.includes('airpods');
              const hasPro = lower.includes('pro');
              const hasMax = lower.includes('max');
              const gen = lower.match(/(\d+)/)?.[1];
              return { hasAirpods, hasPro, hasMax, gen };
            };
            
            const modelInfo = extractCoreInfo(model.modelKo || model.id);
            
            const candidates4 = prices.filter(price => {
              // 🎧 구성별 storage 매칭 필수
              const priceStorage = normalizeStorage(price.variant?.storage);
              if (priceStorage !== normalizedTargetStorage) return false;

              const priceInfo = extractCoreInfo(price.id || '');

              // AirPods 여부 일치
              if (modelInfo.hasAirpods !== priceInfo.hasAirpods) return false;

              // Pro/Max 특수 키워드 일치
              if (modelInfo.hasPro !== priceInfo.hasPro) return false;
              if (modelInfo.hasMax !== priceInfo.hasMax) return false;

              // 세대 일치 (있는 경우)
              if (modelInfo.gen && priceInfo.gen && modelInfo.gen !== priceInfo.gen) return false;

              return true;
            });
            
            // 🔥 가격이 설정된 문서 우선 선택!
            matchingPrice = selectBestPrice(candidates4);
            
            // 🔍 웨어러블 매칭 실패 시 요약 수집
            if (!matchingPrice) {
              wearableMatchFailures.push(model.modelKo);
            }
          }
          
          // 태블릿 매칭 실패 시 요약 수집
          if (!matchingPrice && deviceType === 'tablet') {
            tabletMatchFailures.push(`${model.modelKo}(${storage}/${connectivity})`);
          }

          // 🔥 priceId 생성 시 순수 modelId 사용 (Samsung 태블릿 id에 이미 storage가 포함된 문제 해결)
          // ⚠️ Apple 태블릿은 원본 id 그대로 사용
          // 🔑 P2 핵심: model.id가 + 기호를 누락한 경우 먼저 보정!
          const pureModelIdForPrice = extractPureModelId(correctedModelId, deviceType, model.brand);
          
          // 🔑 P0 핵심 수정: canonical 형식으로 정규화하여 READ/WRITE 경로 일치!
          // [배경] 레거시 형식(smartphone-samsung-...)과 canonical 형식(samsung-smartphone-...)이 혼재
          //        READ는 레거시, WRITE는 canonical → 다른 문서에 저장되는 버그 발생
          // [해결] READ 경로에서도 canonical 형식 사용 → 일관된 문서 참조
          const canonicalModelIdForPrice = canonicalizeModelId(pureModelIdForPrice);
          
          // 🔑 SSOT: priceId 생성 - canonical modelId 사용 + 스마트폰 connectivity는 항상 'LTE'
          const priceIdVariant = {
            storage: storage,
            connectivity: deviceType === 'smartphone' ? 'LTE'  // 🔑 스마트폰은 항상 LTE
              : (deviceType === 'wearable' || connectivity === 'Wi-Fi' || connectivity === '기본')
                ? undefined : connectivity
          };
          // 🔑 P0 수정: canonical modelId로 priceId 생성!
          const priceId = generatePriceId(canonicalModelIdForPrice, priceIdVariant, 'KR');

          // 🔥 중복 priceId 방지 (같은 priceId가 이미 추가되었으면 스킵)
          if (addedPriceIds.has(priceId)) {
            skippedDuplicatePriceIds++;
            return; // 이 조합 스킵
          }
          addedPriceIds.add(priceId);
          
          // 🔑 P1 핵심 버그 수정: canonical priceId와 정확히 일치하는 문서를 최우선 사용!
          // [문제] matchingPrice가 레거시 문서를 참조하면, priceId(canonical)와 불일치
          //        → 저장은 canonical 문서에, 읽기는 레거시 문서에서 → 변경이 반영 안 됨!
          // [해결] canonical priceId로 정확히 매칭되는 문서가 있으면 그것을 사용
          const canonicalPriceMatch = prices.find(p => p.id === priceId);
          const finalMatchingPrice = canonicalPriceMatch || matchingPrice;

          rows.push({
            // 🔑 P0 수정: canonical modelId 사용 (READ/WRITE 경로 일치 보장)
            modelId: canonicalModelIdForPrice,
            modelName: model.modelKo,
            series: model.series,
            storage: storage,
            connectivity: connectivity,
            priceId,
            // 🔑 P1 수정: canonical 문서 우선 사용!
            prices: finalMatchingPrice?.gradePrices || { A: 0, B: 0, C: 0, D: 0 },
            hasPrice: !!finalMatchingPrice,
            isRecommended: model.recommended,
            releaseYear: model.releaseYear || 2024,
            priority: model.priority || 0
          });
        });
      });
    });

    // 📊 요약 로그만 출력 (문제 있을 때만)
    const hasIssues = storageDuplicateModels > 0 || skippedDuplicatePriceIds > 0 || 
                      wearableMatchFailures.length > 0 || tabletMatchFailures.length > 0;
    if (hasIssues) {
      console.log(`📊 [${deviceType}] 테이블 행 생성 완료: ${rows.length}개` +
        (storageDuplicateModels > 0 ? ` | storage중복정리: ${storageDuplicateModels}모델` : '') +
        (skippedDuplicatePriceIds > 0 ? ` | priceId중복스킵: ${skippedDuplicatePriceIds}개` : '') +
        (wearableMatchFailures.length > 0 ? ` | 웨어러블매칭실패: ${wearableMatchFailures.length}개` : '') +
        (tabletMatchFailures.length > 0 ? ` | 태블릿매칭실패: ${tabletMatchFailures.length}개` : '')
      );
    }
    
    // 🔥 DEV 전용: 중복 모델 감지 및 특정 모델 상세 로그
    if (import.meta.env.DEV && deviceType === 'smartphone') {
      // 🔍 1. 중복 모델 감지 (같은 modelName, 다른 modelId)
      const modelNameToIds = new Map<string, Set<string>>();
      const modelNameToPriceIds = new Map<string, Set<string>>();
      
      rows.forEach(r => {
        if (!modelNameToIds.has(r.modelName)) {
          modelNameToIds.set(r.modelName, new Set());
          modelNameToPriceIds.set(r.modelName, new Set());
        }
        modelNameToIds.get(r.modelName)!.add(r.modelId);
        modelNameToPriceIds.get(r.modelName)!.add(r.priceId);
      });
      
      // 중복 모델 리스트 출력
      const duplicateModels: string[] = [];
      modelNameToIds.forEach((ids, name) => {
        if (ids.size > 1) {
          duplicateModels.push(name);
        }
      });
      
      if (duplicateModels.length > 0) {
        console.log('\n' + '🚨'.repeat(40));
        console.log('🚨 [중복 모델 감지] 같은 이름, 다른 ID를 가진 모델:');
        duplicateModels.forEach(name => {
          const ids = Array.from(modelNameToIds.get(name) || []);
          const priceIds = Array.from(modelNameToPriceIds.get(name) || []);
          console.log(`   "${name}":`);
          console.log(`     modelIds: ${ids.join(', ')}`);
          console.log(`     priceIds: ${priceIds.join(', ')}`);
        });
        console.log('🚨'.repeat(40) + '\n');
      }
      
      // 🔍 2. 특정 모델 상세 로그 (갤럭시 폴드, 갤럭시 S25 등 문제 모델)
      const watchModels = ['폴드', 'Fold', 'S25', 'S24'];
      const matchedRows = rows.filter(r => 
        watchModels.some(keyword => r.modelName.includes(keyword))
      );
      
      if (matchedRows.length > 0) {
        console.log('\n' + '═'.repeat(100));
        console.log('🔍 [특정 모델 상세 정보] 주요 모델 현황:');
        console.log('─'.repeat(100));
        
        matchedRows.forEach(r => {
          console.log(`📱 "${r.modelName}" (${r.storage})`);
          console.log(`   ├─ modelId: "${r.modelId}"`);
          console.log(`   ├─ priceId: "${r.priceId}"`);
          console.log(`   ├─ hasPrice: ${r.hasPrice ? '✅' : '❌'}`);
          console.log(`   └─ 가격: A=${r.prices.A?.toLocaleString()}, B=${r.prices.B?.toLocaleString()}, C=${r.prices.C?.toLocaleString()}, D=${r.prices.D?.toLocaleString()}`);
        });
        console.log('═'.repeat(100) + '\n');
      }
      
      // 🔍 3. unified_prices에서 해당 모델의 모든 문서 확인
      const watchPriceKeywords = ['fold', 'galaxy-z-fold'];
      const relatedPrices = prices.filter(p => 
        watchPriceKeywords.some(keyword => 
          (p.id || '').toLowerCase().includes(keyword) ||
          (p.modelId || '').toLowerCase().includes(keyword)
        )
      );
      
      if (relatedPrices.length > 0) {
        console.log('\n' + '═'.repeat(100));
        console.log('🗄️ [unified_prices DB] "폴드" 관련 모든 문서:');
        console.log('─'.repeat(100));
        
        relatedPrices.forEach(p => {
          console.log(`📄 문서 ID: "${p.id}"`);
          console.log(`   ├─ modelId: "${p.modelId}"`);
          console.log(`   ├─ storage: "${p.variant?.storage}"`);
          console.log(`   ├─ connectivity: "${p.variant?.connectivity}"`);
          console.log(`   └─ 가격: A=${p.gradePrices?.A?.toLocaleString()}, B=${p.gradePrices?.B?.toLocaleString()}, C=${p.gradePrices?.C?.toLocaleString()}, D=${p.gradePrices?.D?.toLocaleString()}`);
        });
        console.log('═'.repeat(100) + '\n');
      }
    }

    return rows;
  }, [models, prices, deviceType]);

  // 📊 모델별 그룹 데이터 생성
  const modelGroups = useMemo<ModelGroup[]>(() => {
    const groups: ModelGroup[] = [];
    
    // 🔥 Samsung 태블릿 순수 modelId 추출 헬퍼 (재사용)
    const extractPureModelIdForGroup = (modelId: string, devType: string, modelBrand?: string): string => {
      if (devType !== 'tablet') return modelId;
      const isSamsungTablet = modelBrand === 'Samsung' || 
                              modelId.toLowerCase().includes('samsung') ||
                              modelId.toLowerCase().includes('galaxy');
      if (!isSamsungTablet) return modelId;
      const storageMatch = modelId.match(/-(\d+)(gb|tb)-/i);
      if (!storageMatch) return modelId;
      const storageIndex = modelId.toLowerCase().indexOf(`-${storageMatch[1]}${storageMatch[2].toLowerCase()}-`);
      if (storageIndex === -1) return modelId;
      return modelId.substring(0, storageIndex);
    };

    // 🔑 P2 핵심: model.id가 + 기호를 누락한 경우 model.modelKo/modelEn에서 복원
    // [배경] tableRows에서 이미 보정된 modelId를 사용하므로 그룹핑도 일치해야 함
    const correctModelIdWithPlusSignForGroup = (model: typeof models[0]): string => {
      const modelName = model.modelKo || model.modelEn || '';
      const currentId = model.id;
      const idLower = currentId.toLowerCase();
      
      const hasPlusPlus = modelName.includes('++') || modelName.includes('플러스플러스');
      const hasPlus = !hasPlusPlus && (modelName.includes('+') || modelName.includes('플러스') || modelName.toLowerCase().includes('plus'));
      
      if (hasPlusPlus && !idLower.includes('-plus-plus')) {
        return generateModelId({
          brand: model.brand,
          deviceType: deviceType,
          series: model.series,
          modelEn: (model.modelEn || model.modelKo || '').replace(/플러스/g, '+')
        });
      }
      
      if (hasPlus && !idLower.includes('-plus')) {
        return generateModelId({
          brand: model.brand,
          deviceType: deviceType,
          series: model.series,
          modelEn: (model.modelEn || model.modelKo || '').replace(/플러스/g, '+')
        });
      }
      
      return currentId;
    };

    models.forEach(model => {
      // 🔑 P2 핵심: + 기호 보정 후 canonical modelId로 매칭 (tableRows와 일치)
      const correctedModelId = correctModelIdWithPlusSignForGroup(model);
      const pureModelId = extractPureModelIdForGroup(correctedModelId, deviceType, model.brand);
      const canonicalModelId = canonicalizeModelId(pureModelId);
      
      // 해당 모델의 모든 가격 행 찾기 (canonical modelId로 정확한 매칭)
      const modelRows = tableRows.filter(r => 
        r.modelId === canonicalModelId
      );
      
      const priceRows: PriceRowData[] = modelRows.map(r => ({
        priceId: r.priceId,
        storage: r.storage,
        connectivity: r.connectivity,
        prices: r.prices,
        hasPrice: r.hasPrice && Object.values(r.prices).some(p => p > 0)
      }));

      // 용량/연결성 순서 정렬 (오름차순)
      priceRows.sort((a, b) => {
        const aStorage = parseInt(a.storage.replace(/[^0-9]/g, '')) || 0;
        const bStorage = parseInt(b.storage.replace(/[^0-9]/g, '')) || 0;
        if (aStorage !== bStorage) return aStorage - bStorage;
        if (a.connectivity === 'Wi-Fi') return -1;
        if (b.connectivity === 'Wi-Fi') return 1;
        return 0;
      });

      groups.push({
        model,
        priceRows,
        totalVariants: priceRows.length,
        withPrice: priceRows.filter(r => r.hasPrice).length
      });
    });
    
    return groups;
  }, [models, tableRows, deviceType]);

  // 📊 필터링 및 정렬 적용
  const filteredGroups = useMemo(() => {
    let filtered = [...modelGroups];

    // 검색 필터
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(g =>
        g.model.modelKo.toLowerCase().includes(searchLower) ||
        g.model.modelEn.toLowerCase().includes(searchLower) ||
        g.model.series.toLowerCase().includes(searchLower)
      );
    }

    // 시리즈 필터
    if (filters.series) {
      filtered = filtered.filter(g => g.model.series === filters.series);
    }

    // 가격 상태 필터
    if (filters.priceStatus === 'set') {
      filtered = filtered.filter(g => g.withPrice === g.totalVariants);
    } else if (filters.priceStatus === 'unset') {
      filtered = filtered.filter(g => g.withPrice < g.totalVariants);
    }

    // 추천 모델 필터
    if (filters.recommended === 'yes') {
      filtered = filtered.filter(g => g.model.recommended);
    } else if (filters.recommended === 'no') {
      filtered = filtered.filter(g => !g.model.recommended);
    }

    // 정렬
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sort.field) {
        case 'modelName':
          aValue = a.model.modelKo.toLowerCase();
          bValue = b.model.modelKo.toLowerCase();
          break;
        case 'series':
          aValue = a.model.series.toLowerCase();
          bValue = b.model.series.toLowerCase();
          break;
        case 'priority':
          aValue = a.model.priority || 0;
          bValue = b.model.priority || 0;
          break;
        case 'releaseYear':
          aValue = a.model.releaseYear || 2024;
          bValue = b.model.releaseYear || 2024;
          break;
        default:
          aValue = a.model.priority || 0;
          bValue = b.model.priority || 0;
      }

      if (aValue < bValue) return sort.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [modelGroups, filters, sort]);

  // 📊 인라인 편집용 행 데이터 (전체 tableRows 기준) - 🔥 모델 정보 포함 (안정화)
  const inlineEditRows = useMemo<TableRowData[]>(() => {
    return tableRows.map((row, index) => ({
      priceId: row.priceId,
      modelId: row.modelId,        // 🆕 모델 ID (가격 자동 생성용)
      modelName: row.modelName,
      prices: row.prices,
      rowIndex: index,
      // 🆕 가격 자동 생성 시 필요한 정보
      brand: brand,
      deviceType: deviceType,
      storage: row.storage,
      connectivity: row.connectivity
    }));
  }, [tableRows, brand, deviceType]);

  // 🔥 인라인 가격 편집 훅
  const {
    handleCellChange,
    saveAllChanges,
    discardAllChanges,
    handleNavigate,
    registerCellRef,
    activeCell,
    setActiveCell,
    isSaving: isInlineSaving,
    pendingChanges,
    hasUnsavedChanges,
    changedCellCount,
    isCellChanged,
    getCellValue,
    savedCells,
    error: inlineEditError,
    clearError: clearInlineEditError,
    successMessage: inlineEditSuccess,
    clearSuccessMessage: clearInlineEditSuccess
  } = useInlinePriceEdit({
    rows: inlineEditRows,
    onDataRefresh: async () => {
      const UnifiedPriceService = getUnifiedPriceService();
      UnifiedPriceService.clearCache();
      await loadData(true);
    }
  });

  // 📊 통계 계산
  const statistics = useMemo(() => {
    const allRows = tableRows;
    return {
      totalModels: models.length,
      totalVariants: allRows.length,
      withPrices: allRows.filter(r => r.hasPrice && Object.values(r.prices).some(p => p > 0)).length,
      withoutPrices: allRows.filter(r => !r.hasPrice || Object.values(r.prices).every(p => p === 0)).length,
      recommended: models.filter(m => m.recommended).length,
      bySeries: models.reduce((acc, m) => {
        acc[m.series] = (acc[m.series] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      averagePrices: {
        A: Math.round(allRows.filter(r => (r.prices.A ?? 0) > 0).reduce((sum, r) => sum + (r.prices.A ?? 0), 0) / Math.max(1, allRows.filter(r => (r.prices.A ?? 0) > 0).length)),
        B: Math.round(allRows.filter(r => (r.prices.B ?? 0) > 0).reduce((sum, r) => sum + (r.prices.B ?? 0), 0) / Math.max(1, allRows.filter(r => (r.prices.B ?? 0) > 0).length)),
        C: Math.round(allRows.filter(r => (r.prices.C ?? 0) > 0).reduce((sum, r) => sum + (r.prices.C ?? 0), 0) / Math.max(1, allRows.filter(r => (r.prices.C ?? 0) > 0).length)),
        D: Math.round(allRows.filter(r => (r.prices.D ?? 0) > 0).reduce((sum, r) => sum + (r.prices.D ?? 0), 0) / Math.max(1, allRows.filter(r => (r.prices.D ?? 0) > 0).length)),
        E: Math.round(allRows.filter(r => (r.prices.E ?? 0) > 0).reduce((sum, r) => sum + (r.prices.E ?? 0), 0) / Math.max(1, allRows.filter(r => (r.prices.E ?? 0) > 0).length))
      }
    };
  }, [models, tableRows]);

  // 📊 필터 옵션
  const filterOptions = useMemo(() => ({
    series: Array.from(new Set(models.map(m => m.series))).sort()
  }), [models]);

  // 📊 비활성(삭제된) Variant 목록
  const disabledVariantRows = useMemo(() => {
    const rows: { modelId: string; modelName: string; series: string; storage: string; connectivity: string }[] = [];
    models.forEach(model => {
      const disabled = (model as any).disabledVariants as { storage: string; connectivity?: string }[] | undefined;
      if (!disabled || disabled.length === 0) return;
      disabled.forEach(variant => {
        rows.push({
          modelId: model.id,
          modelName: model.modelKo,
          series: model.series,
          storage: variant.storage,
          connectivity: variant.connectivity || 'Wi-Fi'
        });
      });
    });
    return rows;
  }, [models]);

  // 🔄 모델 확장 토글
  const handleExpandToggle = useCallback((modelId: string, expanded: boolean) => {
    setExpandedModels(prev => {
      const newSet = new Set(prev);
      if (expanded) {
        newSet.add(modelId);
      } else {
        newSet.delete(modelId);
      }
      return newSet;
    });
  }, []);

  // 🔄 전체 펼치기/접기
  const handleExpandAll = useCallback(() => {
    setExpandedModels(new Set(filteredGroups.map(g => g.model.id)));
  }, [filteredGroups]);

  const handleCollapseAll = useCallback(() => {
    setExpandedModels(new Set());
  }, []);

  // 🔄 모델 선택 토글
  const handleModelSelectionChange = useCallback((modelId: string, selected: boolean) => {
    setSelectedModels(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(modelId);
      } else {
        newSet.delete(modelId);
      }
      return newSet;
    });

    // 벌크 편집용 행 선택 동기화
    const modelRows = tableRows.filter(r => r.modelId === modelId);
    setBulkEdit(prev => {
      const newSelectedRows = new Set(prev.selectedRows);
      modelRows.forEach(row => {
        if (selected) {
          newSelectedRows.add(row.priceId);
        } else {
          newSelectedRows.delete(row.priceId);
        }
      });
      return { ...prev, selectedRows: newSelectedRows };
    });
  }, [tableRows]);

  // 🔄 필터 리셋
  const resetFilters = useCallback(() => {
    setFilters({
      search: '',
      series: '',
      priceStatus: 'all',
      recommended: 'all'
    });
  }, []);

  // 💾 벌크 가격 업데이트 실행
  const executeBulkUpdate = useCallback(async () => {
    try {
      setSaving(new Set(Array.from(bulkEdit.selectedRows)));
      const UnifiedPriceService = getUnifiedPriceService();

      const updatePromises = Array.from(bulkEdit.selectedRows).map(priceId => {
        const gradePrices: Record<string, number> = { A: 0, B: 0, C: 0, D: 0, E: 0 };
        bulkEdit.applyGrades.forEach(grade => {
          gradePrices[grade] = bulkEdit.bulkPrices[grade] || 0;
        });
        const existingRow = tableRows.find(r => r.priceId === priceId);
        if (existingRow) {
          (['A', 'B', 'C', 'D', 'E'] as DeviceGrade[]).forEach(grade => {
            if (!bulkEdit.applyGrades.has(grade)) {
              gradePrices[grade] = existingRow.prices[grade] || 0;
            }
          });
        }
        return UnifiedPriceService.updatePrice({
          priceId,
          gradePrices,
          memo: `벌크 업데이트 - ${new Date().toLocaleString()}`
        }, 'admin');
      });

      await Promise.all(updatePromises);
      setSuccessMessage(`${bulkEdit.selectedRows.size}개 가격이 벌크 업데이트되었습니다.`);
      setBulkDialogOpen(false);
      setSelectedModels(new Set());
      setBulkEdit(prev => ({ ...prev, selectedRows: new Set() }));
      await loadData(true);

    } catch (err) {
      console.error('❌ 벌크 업데이트 실패:', err);
      setError(`벌크 업데이트 실패: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSaving(new Set());
    }
  }, [bulkEdit, tableRows, loadData]);

  // 🆕 벌크 제조사 변경 실행 (완전 삭제 + 재생성 방식)
  const executeBulkBrandChange = useCallback(async () => {
    if (selectedModels.size === 0) {
      setError('변경할 모델을 선택해주세요.');
      return;
    }

    try {
      setSaving(new Set(Array.from(selectedModels)));
      const { db } = await import('../../../../firebase');
      const { doc, setDoc, deleteDoc, collection, query, where, getDocs, addDoc, serverTimestamp } = await import('firebase/firestore');
      const { generateModelId, generatePriceId } = await import('../../../../types/deviceCatalog');

      const selectedModelIds = Array.from(selectedModels);
      let totalModelsChanged = 0;
      let totalPriceDocsMigrated = 0;

      for (const modelId of selectedModelIds) {
        const model = modelGroups.find(g => g.model.id === modelId)?.model;
        if (!model) continue;

        const oldBrand = model.brand;
        const newBrand = bulkBrandChangeTarget;

        if (oldBrand === newBrand) {
          console.log(`⏭️ 모델 ${model.modelKo}: 제조사가 이미 ${newBrand}입니다. 건너뜁니다.`);
          continue;
        }

        console.log(`🔥 모델 ${model.modelKo}: 제조사 변경 ${oldBrand} → ${newBrand} (삭제+재생성)`);

        // 1. 새로운 modelId 생성
        const newModelId = generateModelId({
          brand: newBrand,
          deviceType: model.deviceType,
          series: model.series,
          modelEn: model.modelEn
        });

        console.log(`📌 새 modelId 생성: ${modelId} → ${newModelId}`);

        // 2. 기존 가격 문서 조회
        const oldPricesQuery = query(
          collection(db, 'unified_prices'),
          where('modelId', '==', modelId)
        );
        const oldPricesSnapshot = await getDocs(oldPricesQuery);
        console.log(`📊 복사할 가격 문서 수: ${oldPricesSnapshot.docs.length}`);

        // 3. 새 모델 문서 생성
        const newModelRef = doc(db, 'device_catalog', newModelId);
        await setDoc(newModelRef, {
          ...model,
          id: newModelId,
          brand: newBrand,
          updatedAt: new Date(),
          updatedBy: 'admin',
          createdAt: model.createdAt || new Date()
        });
        console.log(`✅ 새 모델 문서 생성: ${newModelId}`);

        // 4. 가격 문서 복사 (새 modelId와 새 priceId로)
        for (const oldPriceDoc of oldPricesSnapshot.docs) {
          const oldPriceData = oldPriceDoc.data();

          // 새 priceId 생성
          const newPriceId = generatePriceId(
            newModelId,
            {
              storage: oldPriceData.variant?.storage || '기본',
              connectivity: oldPriceData.variant?.connectivity
            },
            'KR'
          );

          const newPriceRef = doc(db, 'unified_prices', newPriceId);
          await setDoc(newPriceRef, {
            ...oldPriceData,
            id: newPriceId,
            modelId: newModelId,
            brand: newBrand,
            updatedAt: new Date(),
            updatedBy: 'admin',
            memo: `벌크 제조사 변경으로 인한 재생성: ${oldBrand} → ${newBrand} - ${new Date().toLocaleString()}`,
            legacy: {
              originalModelId: modelId,
              originalPriceId: oldPriceDoc.id,
              migratedAt: new Date(),
              reason: 'Bulk brand change migration'
            }
          });
        }

        totalPriceDocsMigrated += oldPricesSnapshot.docs.length;
        console.log(`💰 ${oldPricesSnapshot.docs.length}개 가격 문서 복사 완료`);

        // 5. 기존 가격 문서 삭제
        for (const oldPriceDoc of oldPricesSnapshot.docs) {
          const oldPriceRef = doc(db, 'unified_prices', oldPriceDoc.id);
          await deleteDoc(oldPriceRef);
        }
        console.log(`🗑️ 기존 가격 문서 ${oldPricesSnapshot.docs.length}개 삭제 완료`);

        // 6. 기존 모델 문서 삭제
        const oldModelRef = doc(db, 'device_catalog', modelId);
        await deleteDoc(oldModelRef);
        console.log(`🗑️ 기존 모델 문서 삭제: ${modelId}`);

        // 7. 제조사 변경 로그 기록
        await addDoc(collection(db, 'brand_change_logs'), {
          oldModelId: modelId,
          newModelId: newModelId,
          modelKo: model.modelKo,
          modelEn: model.modelEn,
          deviceType: model.deviceType,
          oldBrand: oldBrand,
          newBrand: newBrand,
          affectedPriceDocuments: oldPricesSnapshot.docs.length,
          migrationType: 'DELETE_AND_RECREATE',
          changedBy: 'admin',
          changedAt: serverTimestamp(),
          reason: 'Bulk brand change via FirebasePriceManagementGrid'
        });

        totalModelsChanged++;
        console.log(`✅ 모델 재생성 완료: ${model.modelKo}`);
      }

      // 캐시 정리
      DeviceCatalogService.clearCache();
      const UnifiedPriceService = getUnifiedPriceService();
      UnifiedPriceService.clearCache();

      setSuccessMessage(
        `✅ ${totalModelsChanged}개 모델이 ${bulkBrandChangeTarget}로 이동되었습니다.\n\n` +
        `처리 방식: 완전 삭제 + 재생성\n` +
        `• 이전된 가격 문서: ${totalPriceDocsMigrated}개\n` +
        `• 새로운 모델ID로 재생성됨\n\n` +
        `"${bulkBrandChangeTarget}" 메뉴로 이동하여 확인하세요.`
      );
      setBulkBrandChangeDialogOpen(false);
      setSelectedModels(new Set());
      await loadData(true);

    } catch (err) {
      console.error('❌ 벌크 제조사 변경 실패:', err);
      setError(`벌크 제조사 변경 실패: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSaving(new Set());
    }
  }, [selectedModels, bulkBrandChangeTarget, modelGroups, loadData]);

  // 📊 비활성 Variant 복원
  const restoreDisabledVariant = useCallback(async (modelId: string, storage: string, connectivity: string) => {
    try {
      setLoading(true);
      const targetModel = models.find(m => m.id === modelId);
      if (!targetModel) {
        setError('대상 모델을 찾을 수 없습니다.');
        return;
      }

      const existing = ((targetModel as any).disabledVariants || []) as { storage: string; connectivity?: ConnectivityType }[];
      const updated = existing.filter(v => !(v.storage === storage && ((v.connectivity || 'Wi-Fi') === (connectivity || 'Wi-Fi'))));

      await DeviceCatalogService.updateModel(modelId, { disabledVariants: updated }, 'admin');
      DeviceCatalogService.clearCache();
      await loadData(true);
      setSuccessMessage(`"${targetModel.modelKo}"의 ${storage} ${connectivity} 구성이 복원되었습니다.`);
    } catch (err) {
      setError(`복원 실패: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [models, loadData]);

  // 📊 데이터 내보내기 (엑셀 한글 호환 - UTF-8 BOM 추가)
  const exportData = useCallback(() => {
    const exportRows = tableRows.map(row => ({
      모델명: row.modelName,
      시리즈: row.series,
      용량: row.storage,
      연결성: row.connectivity,
      A등급: row.prices.A,
      B등급: row.prices.B,
      C등급: row.prices.C,
      D등급: row.prices.D,
      가격설정: row.hasPrice ? '설정됨' : '미설정',
      추천모델: row.isRecommended ? '추천' : '일반'
    }));

    // CSV 값에 쉼표나 줄바꿈이 있을 경우 따옴표로 감싸기
    const escapeCSV = (value: any): string => {
      const str = String(value);
      if (str.includes(',') || str.includes('\n') || str.includes('"')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [
      Object.keys(exportRows[0]).map(escapeCSV).join(','),
      ...exportRows.map(row => Object.values(row).map(escapeCSV).join(','))
    ].join('\n');

    // 🔥 UTF-8 BOM 추가 (엑셀에서 한글 깨짐 방지)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${brand}-${deviceType}-prices-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();

    setSuccessMessage(`${brand} ${deviceType} 가격 데이터가 내보내기되었습니다.`);
  }, [brand, deviceType, tableRows]);

  // 🎨 가격 테이블 렌더러 (ModelCard에 전달)
  const renderPriceTable = useCallback((priceRows: PriceRowData[], modelId: string, modelName: string) => {
    // 이 모델의 첫 번째 행의 전역 인덱스 찾기
    const firstRowIndex = tableRows.findIndex(r => r.modelId === modelId);
    
    return (
      <ExpandablePriceTable
        priceRows={priceRows}
        deviceType={deviceType}
        modelId={modelId}
        modelName={modelName}
        rowIndexOffset={firstRowIndex >= 0 ? firstRowIndex : 0}
        onCellChange={handleCellChange}
        onNavigate={handleNavigate}
        registerCellRef={registerCellRef}
        activeCell={activeCell}
        setActiveCell={setActiveCell}
        isCellChanged={isCellChanged}
        getCellValue={getCellValue}
        savedCells={savedCells}
        isSaving={isInlineSaving}
        savingPriceIds={saving}
        // 🔥 리렌더링 트리거용 (변경된 셀 개수)
        changedCellCount={changedCellCount}
        savedCellCount={savedCells.size}
        onHistoryClick={(priceId, name) => setHistoryDialog({ open: true, priceId, modelName: name })}
        onDeleteClick={(priceId, storage, connectivity) => {
          const model = models.find(m => m.id === modelId);
          if (model) {
            setDeleteDialog({
              open: true,
              modelId,
              modelName: model.modelKo,
              targetPriceId: priceId,
              targetVariant: { storage, connectivity }
            });
          }
        }}
      />
    );
  }, [
    deviceType, tableRows, handleCellChange, handleNavigate, registerCellRef,
    activeCell, setActiveCell, isCellChanged, getCellValue, savedCells,
    isInlineSaving, saving, models
  ]);

  // 🎨 로딩 상태
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px">
        <CircularProgress size={50} />
        <Typography variant="h6" sx={{ ml: 2, color: '#6b7280' }}>
          Firebase 데이터 로딩 중...
        </Typography>
      </Box>
    );
  }

  // 🎨 에러 상태
  if (error && !models.length) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <Typography variant="h6">데이터 로딩 실패</Typography>
        <Typography variant="body2">{error}</Typography>
        <Button variant="outlined" onClick={() => loadData()} sx={{ mt: 1 }} startIcon={<RefreshIcon />}>
          다시 시도
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      {/* 🔝 컴팩트한 헤더 및 빠른 액션 바 */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          {/* 좌측: 핵심 정보 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MoneyIcon color="primary" />
                실시간 가격 관리
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {brand} {deviceType === 'tablet' ? '태블릿' : deviceType === 'smartphone' ? '스마트폰' : '웨어러블'} · 
                {statistics.totalModels}개 모델 · {statistics.totalVariants}개 구성
              </Typography>
            </Box>
            
            {/* 핵심 통계 */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Chip label={`설정됨 ${statistics.withPrices}`} color="success" size="small" variant="outlined" />
              <Chip label={`미설정 ${statistics.withoutPrices}`} color="warning" size="small" variant="outlined" />
            </Box>
          </Box>

          {/* 우측: 액션 버튼들 */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* 미저장 변경사항 표시 */}
            {hasUnsavedChanges && (
              <Box sx={{ 
                display: 'flex', alignItems: 'center', gap: 1,
                bgcolor: '#fef3c7', px: 2, py: 0.5, borderRadius: 2, border: '2px solid #f59e0b'
              }}>
                <WarningIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                <Typography variant="body2" fontWeight={600} color="#b45309">
                  {changedCellCount}개 변경됨
                </Typography>
                <Button
                  variant="contained" color="success" size="small"
                  onClick={saveAllChanges} disabled={isInlineSaving}
                  startIcon={isInlineSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                >
                  {isInlineSaving ? '저장 중...' : '저장'}
                </Button>
                <Button variant="outlined" color="warning" size="small" onClick={discardAllChanges} disabled={isInlineSaving}>
                  취소
                </Button>
              </Box>
            )}

            {/* 전체 펼치기/접기 */}
            <Tooltip title="전체 펼치기">
              <IconButton size="small" onClick={handleExpandAll}>
                <ExpandAllIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="전체 접기">
              <IconButton size="small" onClick={handleCollapseAll}>
                <CollapseAllIcon />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem />

            {/* 모델 추가 */}
            <Button
              startIcon={<ImportIcon />}
              onClick={() => setAddModelDialogOpen(true)}
              size="small" variant="outlined" color="success"
            >
              모델 추가
            </Button>

            {/* 비활성 구성 관리 */}
            <Button
              startIcon={<HideIcon />}
              onClick={() => setDisabledDialogOpen(true)}
              size="small" variant="outlined" color="secondary"
            >
              비활성 구성 ({disabledVariantRows.length})
            </Button>

            {/* 벌크 편집 */}
            <Button
              startIcon={<BulkIcon />}
              onClick={() => setBulkDialogOpen(true)}
              disabled={bulkEdit.selectedRows.size === 0}
              size="small" variant="outlined" color="primary"
            >
              벌크 편집 ({selectedModels.size})
            </Button>

            {/* 🆕 벌크 제조사 변경 */}
            {selectedModels.size > 0 && (
              <Button
                startIcon={<BulkIcon />}
                onClick={() => setBulkBrandChangeDialogOpen(true)}
                size="small" variant="outlined" color="warning"
              >
                제조사 변경 ({selectedModels.size})
              </Button>
            )}

            {/* 🗑️ 선택 삭제 */}
            {selectedModels.size > 0 && (
              <Button
                startIcon={<DeleteIcon />}
                onClick={() => {
                  // 선택된 모델이 1개면 바로 삭제 다이얼로그
                  if (selectedModels.size === 1) {
                    const modelId = Array.from(selectedModels)[0];
                    const model = models.find(m => m.id === modelId);
                    if (model) {
                      setDeleteDialog({ open: true, modelId, modelName: model.modelKo });
                    }
                  } else {
                    // 다중 선택 시 경고
                    setError(`다중 모델 삭제는 지원되지 않습니다. 모델을 하나씩 삭제해주세요. (선택된 모델: ${selectedModels.size}개)`);
                  }
                }}
                size="small" variant="outlined" color="error"
              >
                선택 삭제 ({selectedModels.size})
              </Button>
            )}

            {/* 통계 */}
            <Button
              startIcon={<StatsIcon />}
              onClick={() => setShowStats(!showStats)}
              size="small" variant="outlined" color="info"
            >
              {showStats ? '통계 숨기기' : '통계'}
            </Button>

            {/* 내보내기 */}
            <Tooltip title="CSV 내보내기">
              <IconButton size="small" onClick={exportData}>
                <ExportIcon />
              </IconButton>
            </Tooltip>

            {/* 새로고침 */}
            <Button startIcon={<RefreshIcon />} onClick={() => loadData(true)} size="small" variant="outlined">
              새로고침
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* 🔍 필터 패널 - 항상 표시 */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="모델명 검색"
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>가격 상태</InputLabel>
            <Select value={filters.priceStatus} onChange={(e) => setFilters(prev => ({ ...prev, priceStatus: e.target.value as any }))}>
              <MenuItem value="all">전체</MenuItem>
              <MenuItem value="set">설정 완료</MenuItem>
              <MenuItem value="unset">미설정 있음</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>시리즈</InputLabel>
            <Select value={filters.series} onChange={(e) => setFilters(prev => ({ ...prev, series: e.target.value }))}>
              <MenuItem value="">전체</MenuItem>
              {filterOptions.series.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <Button startIcon={<FilterIcon />} onClick={resetFilters} size="small" variant="outlined">
            초기화
          </Button>
          <Typography variant="body2" color="text.secondary">
            표시중: <strong>{filteredGroups.length}</strong>개 모델
          </Typography>
        </Box>
      </Paper>

      {/* 📋 모델 카드 리스트 */}
      <Box sx={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
        {filteredGroups.map((group, index) => (
          <ModelCard
            key={group.model.id}
            model={group.model}
            priceRows={group.priceRows}
            deviceType={deviceType}
            brand={brand}
            index={index}
            isSelected={selectedModels.has(group.model.id)}
            onSelectionChange={handleModelSelectionChange}
            expanded={expandedModels.has(group.model.id)}
            onExpandChange={handleExpandToggle}
            onSettingsClick={(model) => setEditModelDialog({ open: true, model })}
            onVariantManageClick={(model) => setVariantDrawer({ open: true, model })}
            onDeleteClick={(model) => setDeleteDialog({ open: true, modelId: model.id, modelName: model.modelKo })}
            renderPriceTable={(priceRows) => renderPriceTable(priceRows, group.model.id, group.model.modelKo)}
            // 🔥 리렌더링 트리거용 (변경된 셀 개수)
            changedCellCount={changedCellCount}
            savedCellCount={savedCells.size}
          />
        ))}

        {/* 빈 데이터 */}
        {filteredGroups.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <WarningIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              {models.length === 0 ? '등록된 모델이 없습니다' : '필터 조건에 맞는 모델이 없습니다'}
            </Typography>
            {filters.search || filters.series || filters.priceStatus !== 'all' ? (
              <Button onClick={resetFilters} sx={{ mt: 1 }} startIcon={<RefreshIcon />}>
                필터 초기화
              </Button>
            ) : (
              <Button onClick={() => setAddModelDialogOpen(true)} sx={{ mt: 1 }} startIcon={<ImportIcon />} variant="contained">
                첫 모델 추가하기
              </Button>
            )}
          </Paper>
        )}
      </Box>

      {/* 🔥 푸터: 편집 안내 */}
      <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          📊 <strong>엑셀 스타일 편집:</strong> 모델 카드 펼치기 → 셀 클릭 → 값 입력 → Enter/Tab으로 이동 → 
          <span style={{ color: '#f59e0b', fontWeight: 600 }}>변경된 셀은 노란색</span> → 
          <strong style={{ color: '#10b981' }}>저장 버튼</strong>으로 일괄 저장
        </Typography>
      </Paper>

      {/* 🔥 다이얼로그들 */}
      
      {/* 모델 추가 */}
      <AddModelDialog
        open={addModelDialogOpen}
        onClose={() => setAddModelDialogOpen(false)}
        brand={brand}
        deviceType={deviceType}
        onSuccess={(forceRefresh = true) => loadData(forceRefresh)}
        onNavigateToVariantManager={(modelId) => {
          // 🆕 해당 모델 찾아서 용량 관리 Drawer 열기
          const targetModel = models.find(m => m.id === modelId);
          if (targetModel) {
            setAddModelDialogOpen(false);
            setVariantDrawer({ open: true, model: targetModel });
            // 해당 모델 카드 확장
            setExpandedModels(prev => new Set(prev).add(modelId));
          }
        }}
      />

      {/* 모델 수정 */}
      <EditModelDialog
        open={editModelDialog.open}
        model={editModelDialog.model}
        initialPrices={editModelDialog.initialPrices}
        targetConfig={editModelDialog.targetConfig}
        brand={brand}
        deviceType={deviceType}
        onClose={() => setEditModelDialog({ open: false, model: null })}
        onSuccess={() => {
          setSuccessMessage('모델 정보가 수정되었습니다.');
          loadData(true);
        }}
      />

      {/* 모델/구성 삭제 */}
      <DeleteModelDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, modelId: '', modelName: '' })}
        modelId={deleteDialog.modelId}
        modelName={deleteDialog.modelName}
        targetPriceId={deleteDialog.targetPriceId}
        targetVariant={deleteDialog.targetVariant}
        brand={brand}
        deviceType={deviceType}
        onSuccess={() => {
          setSuccessMessage(deleteDialog.targetPriceId 
            ? `구성이 삭제되었습니다.` 
            : `"${deleteDialog.modelName}" 모델이 삭제되었습니다.`);
          loadData(true);
        }}
      />

      {/* 용량 관리 Drawer */}
      <VariantManagerDrawer
        open={variantDrawer.open}
        onClose={() => setVariantDrawer({ open: false, model: null })}
        model={variantDrawer.model}
        deviceType={deviceType}
        brand={brand}
        onSuccess={() => {
          setSuccessMessage('용량 설정이 변경되었습니다.');
          loadData(true);
        }}
      />

      {/* 가격 히스토리 */}
      <PriceHistoryDialog
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ open: false, priceId: '', modelName: '' })}
        priceId={historyDialog.priceId}
        modelName={historyDialog.modelName}
      />

      {/* 비활성 구성 관리 */}
      <Dialog open={disabledDialogOpen} onClose={() => setDisabledDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HideIcon color="action" />
            비활성(삭제된) 구성 관리
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {disabledVariantRows.length === 0 ? (
            <Alert severity="info">현재 비활성화된 구성이 없습니다.</Alert>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                삭제된 구성을 복원하면 가격 테이블에 다시 표시됩니다.
              </Alert>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {disabledVariantRows.map((row, idx) => (
                  <Paper key={`${row.modelId}-${row.storage}-${row.connectivity}`} variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{row.modelName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.series} · {row.storage} · {row.connectivity}
                      </Typography>
                    </Box>
                    <Button
                      size="small" variant="outlined" color="primary"
                      onClick={() => restoreDisabledVariant(row.modelId, row.storage, row.connectivity)}
                    >
                      복원
                    </Button>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisabledDialogOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 벌크 편집 다이얼로그 */}
      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BulkIcon color="primary" />
            벌크 가격 편집
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            선택된 <strong>{bulkEdit.selectedRows.size}개 구성</strong>에 일괄 적용할 가격을 설정하세요.
          </Alert>
          <Grid container spacing={2}>
            {(['A', 'B', 'C', 'D'] as DeviceGrade[]).map(grade => (
              <Grid item xs={6} key={grade}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={bulkEdit.applyGrades.has(grade)}
                      onChange={(e) => {
                        setBulkEdit(prev => {
                          const newApplyGrades = new Set(prev.applyGrades);
                          if (e.target.checked) newApplyGrades.add(grade);
                          else newApplyGrades.delete(grade);
                          return { ...prev, applyGrades: newApplyGrades };
                        });
                      }}
                    />
                  }
                  label={`${getGradeLabel(grade)} 적용`}
                />
                <TextField
                  fullWidth size="small"
                  label={`${getGradeLabel(grade)} 가격`}
                  value={bulkEdit.bulkPrices[grade]}
                  onChange={(e) => {
                    const value = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0;
                    setBulkEdit(prev => ({ ...prev, bulkPrices: { ...prev.bulkPrices, [grade]: value } }));
                  }}
                  disabled={!bulkEdit.applyGrades.has(grade)}
                  InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                  sx={{ mt: 1 }}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>취소</Button>
          <Button onClick={executeBulkUpdate} variant="contained" disabled={bulkEdit.applyGrades.size === 0} startIcon={<SaveIcon />}>
            일괄 적용
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🆕 벌크 제조사 변경 다이얼로그 */}
      <Dialog open={bulkBrandChangeDialogOpen} onClose={() => setBulkBrandChangeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            벌크 제조사 변경
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              선택된 <strong>{selectedModels.size}개 모델</strong>의 제조사를 일괄 변경합니다.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
              <strong>처리 방식:</strong> 완전 삭제 + 재생성
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>기존 모델 및 가격 데이터가 삭제됩니다.</li>
              <li>새로운 모델ID로 모델이 재생성됩니다.</li>
              <li>모든 가격 데이터가 새 ID로 복사됩니다.</li>
              <li>변경 이력이 brand_change_logs에 기록됩니다.</li>
              <li>이 작업은 되돌릴 수 있습니다 (다시 제조사를 변경하면 됩니다).</li>
            </Box>
          </Alert>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
              변경할 모델 목록:
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto', bgcolor: '#f8fafc' }}>
              {Array.from(selectedModels).map(modelId => {
                const model = modelGroups.find(g => g.model.id === modelId)?.model;
                return model ? (
                  <Chip
                    key={modelId}
                    label={`${model.modelKo} (${model.brand})`}
                    size="small"
                    sx={{ m: 0.5 }}
                  />
                ) : null;
              })}
            </Paper>
          </Box>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>변경할 제조사 선택</InputLabel>
            <Select
              value={bulkBrandChangeTarget}
              label="변경할 제조사 선택"
              onChange={(e) => setBulkBrandChangeTarget(e.target.value as 'Apple' | 'Samsung' | 'LG')}
            >
              <MenuItem value="Apple">Apple</MenuItem>
              <MenuItem value="Samsung">Samsung</MenuItem>
              <MenuItem value="LG">LG</MenuItem>
            </Select>
          </FormControl>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>💡 팁:</strong> 삼성 페이지에 잘못 분류된 LG 모델들을 한 번에 이동할 수 있습니다.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setBulkBrandChangeDialogOpen(false)} color="inherit">
            취소
          </Button>
          <Button
            onClick={executeBulkBrandChange}
            variant="contained"
            color="warning"
            startIcon={<CheckIcon />}
            disabled={saving.size > 0}
          >
            {saving.size > 0 ? '변경 중...' : `${selectedModels.size}개 모델 제조사 변경`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 통계 모달 */}
      <Modal open={showStats} onClose={() => setShowStats(false)} closeAfterTransition slots={{ backdrop: Backdrop }}>
        <Fade in={showStats}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: { xs: '95%', md: '70%' }, maxWidth: 900, maxHeight: '90vh',
            bgcolor: 'background.paper', borderRadius: 3, boxShadow: 24, overflow: 'hidden'
          }}>
            <Box sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: 'white', p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" fontWeight={700}>📊 가격 통계 분석</Typography>
              <IconButton onClick={() => setShowStats(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
            </Box>
            <Box sx={{ p: 3, maxHeight: '70vh', overflow: 'auto' }}>
              <Grid container spacing={3}>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', textAlign: 'center' }}>
                    <CardContent>
                      <Typography variant="h4" fontWeight={800}>{statistics.withPrices}</Typography>
                      <Typography variant="body2">가격 설정 완료</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', textAlign: 'center' }}>
                    <CardContent>
                      <Typography variant="h4" fontWeight={800}>{statistics.withoutPrices}</Typography>
                      <Typography variant="body2">가격 미설정</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white', textAlign: 'center' }}>
                    <CardContent>
                      <Typography variant="h4" fontWeight={800}>{statistics.recommended}</Typography>
                      <Typography variant="body2">추천 모델</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: 'white', textAlign: 'center' }}>
                    <CardContent>
                      <Typography variant="h4" fontWeight={800}>{statistics.totalModels}</Typography>
                      <Typography variant="body2">총 모델 수</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* 등급별 평균 가격 */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom>등급별 평균 가격</Typography>
                  {(['A', 'B', 'C', 'D', 'E'] as DeviceGrade[]).map((grade, idx) => {
                    const colors = ['#10b981', '#0ea5e9', '#f59e0b', '#ef4444', '#6b7280'];
                    const maxPrice = Math.max(...Object.values(statistics.averagePrices));
                    const percentage = maxPrice > 0 ? (statistics.averagePrices[grade] / maxPrice) * 100 : 0;
                    return (
                      <Box key={grade} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography fontWeight={600}>{getGradeLabel(grade)}</Typography>
                          <Typography fontFamily="monospace" fontWeight={700}>
                            {statistics.averagePrices[grade].toLocaleString()}원
                          </Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={percentage} sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: colors[idx] } }} />
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* 성공 스낵바 */}
      <Snackbar
        open={!!successMessage || !!inlineEditSuccess}
        autoHideDuration={3000}
        onClose={() => { setSuccessMessage(null); clearInlineEditSuccess(); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => { setSuccessMessage(null); clearInlineEditSuccess(); }}>
          {successMessage || inlineEditSuccess}
        </Alert>
      </Snackbar>

      {/* 에러 스낵바 */}
      <Snackbar
        open={!!error || !!inlineEditError}
        autoHideDuration={6000}
        onClose={() => { setError(null); clearInlineEditError(); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => { setError(null); clearInlineEditError(); }}>
          {error || inlineEditError}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default FirebasePriceManagementGrid;
